import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  Tooltip,
  Row,
  Col,
  Typography
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
  WhatsAppOutlined,
  MailOutlined,
  UploadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import {
  subscribeToQuotations,
  deleteQuotation,
  updateQuotationStatus,
  getQuotationStats,
  QUOTATION_STATUSES,
  QUOTATION_STATUS_LABELS,
  QUOTATION_STATUS_COLORS,
  QUOTATION_TYPE_LABELS
} from '../../firebase/quotationService';
import { formatMultipleDatesForPDF } from '../../utils/multipleDateUtils';
import { usesUnifiedDates, getDatesForPDF } from '../../utils/unifiedDateStorage';
import {
  subscribeToQuotes,
  QUOTE_STATUSES,
  STATUS_LABELS as QUOTE_STATUS_LABELS,
  STATUS_COLORS as QUOTE_STATUS_COLORS
} from '../../firebase/quotesService';
import { generateQuotationPDF } from './QuotationPDF';
import QuotationForm from './QuotationForm';
import { subscribeToInvoices } from '../../firebase/invoiceService';
import InvoiceForm from '../Invoice/InvoiceForm';
import WhatsAppModal from '../WhatsApp/WhatsAppModal';
import EmailModal from '../Email/EmailModal';
import CSVQuotationImport from '../Admin/CSVQuotationImport';
import './QuotationList.css';

const { Option } = Select;
const { Title, Text } = Typography;

const QuotationList = ({ dateRange, onDateRangeChange }) => {
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [stats, setStats] = useState({});
  const [quotationsUnsubscribe, setQuotationsUnsubscribe] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedQuotationForInvoice, setSelectedQuotationForInvoice] = useState(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppQuotation, setWhatsAppQuotation] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailQuotation, setEmailQuotation] = useState(null);
  const [showQuoteRequestModal, setShowQuoteRequestModal] = useState(false);
  const [availableQuoteRequests, setAvailableQuoteRequests] = useState([]);
  const [selectedQuoteRequest, setSelectedQuoteRequest] = useState(null);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  // Subscribe to quotations when dateRange changes
  useEffect(() => {
    // Always subscribe, even when dateRange is null (for "All Time")
    if (!dateRange) {
      return; // Wait for dateRange to be initialized
    }

    // Unsubscribe from previous subscription
    if (quotationsUnsubscribe) {
      quotationsUnsubscribe();
    }
    
    // Create new subscription with date range
    // When both dates are null, this means "All Time" - fetch all quotations
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    
    console.log('📊 QuotationList: Setting up subscription with date range:', {
      label: dateRange.label,
      hasStartDate: !!dateRange.startDate,
      hasEndDate: !!dateRange.endDate,
      isAllTime: !dateRange.startDate && !dateRange.endDate
    });
    
    const newUnsubscribe = subscribeToQuotations((quotationsData) => {
      setQuotations(quotationsData);
      setFilteredQuotations(quotationsData);
      setLoading(false);
    }, options);
    
    setQuotationsUnsubscribe(() => newUnsubscribe);
    
    // Update stats with date range
    getQuotationStats(options).then(setStats);

    return () => {
      if (newUnsubscribe) {
        newUnsubscribe();
      }
    };
  }, [dateRange]);

  useEffect(() => {
    // Subscribe to invoices data to track which quotations have invoices
    const unsubscribeInvoices = subscribeToInvoices((invoicesData) => {
      setInvoices(invoicesData);
    });

    // Subscribe to quote requests to get available ones
    const unsubscribeQuotes = subscribeToQuotes((quotesData) => {
      // Filter quote requests that don't have quotations created and are not closed
      const availableRequests = quotesData.filter(quote => 
        quote.status !== QUOTE_STATUSES.CLOSED && 
        quote.status !== QUOTE_STATUSES.QUOTED &&
        !quotations.some(quotation => quotation.quoteRequestId === quote.id)
      );
      setAvailableQuoteRequests(availableRequests);
    });

    return () => {
      unsubscribeInvoices();
      unsubscribeQuotes();
    };
  }, [quotations]);

  useEffect(() => {
    // Filter quotations based on search term, status, and type
    let filtered = quotations;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(quotation =>
        quotation.companyName?.toLowerCase().includes(searchLower) ||
        quotation.attn?.toLowerCase().includes(searchLower) ||
        quotation.email?.toLowerCase().includes(searchLower) ||
        quotation.quotationNumber?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(quotation => quotation.status === statusFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(quotation => quotation.quotationType === typeFilter);
    }

    setFilteredQuotations(filtered);
  }, [quotations, searchTerm, statusFilter, typeFilter]);

  const handleCreateQuotation = () => {
    setSelectedQuotation(null);
    setShowCreateModal(true);
  };

  const handleEditQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowEditModal(true);
  };

  const handleDeleteQuotation = async (quotationId) => {
    Modal.confirm({
      title: 'Delete Quotation',
      content: 'Are you sure you want to delete this quotation? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        const success = await deleteQuotation(quotationId);
        if (success) {
          message.success('Quotation deleted successfully');
          // Update stats with current date range
          const options = {};
          if (dateRange.startDate) {
            options.startDate = dateRange.startDate;
          }
          if (dateRange.endDate) {
            options.endDate = dateRange.endDate;
          }
          getQuotationStats(options).then(setStats);
        } else {
          message.error('Failed to delete quotation');
        }
      }
    });
  };

  const handleStatusChange = async (quotationId, newStatus) => {
    const success = await updateQuotationStatus(quotationId, newStatus);
    if (success) {
      message.success('Status updated successfully');
      // Update stats with current date range
      const options = {};
      if (dateRange.startDate) {
        options.startDate = dateRange.startDate;
      }
      if (dateRange.endDate) {
        options.endDate = dateRange.endDate;
      }
      getQuotationStats(options).then(setStats);
    } else {
      message.error('Failed to update status');
    }
  };

  const handleGeneratePDF = async (quotation) => {
    try {
      // Helper function to format duration with unit
      const formatDuration = (value, unit) => {
        if (!value) return '';
        return `${value} ${unit}${value > 1 ? 's' : ''}`;
      };
      
      // Helper function to format date
      const formatDate = (date) => {
        if (!date) return '';
        return moment(date).format('DD/MM/YYYY');
      };

      const quotationData = {
        quotationNumber: quotation.quotationNumber,
        date: moment().format('DD/MM/YY'),
        // Include creation and modification timestamps if available
        createdAt: quotation.createdAt || null,
        updatedAt: quotation.updatedAt || null,
        companyName: quotation.companyName,
        attn: quotation.attn,
        tel: quotation.tel,
        email: quotation.email,
        billingAddress: quotation.billingAddress,
        quotationType: quotation.quotationType,
        siteAddress: quotation.siteAddress,
        dates: quotation.dates || [], // Add unified dates support
        
        // Date input type information for PDF logic
        leadTimeInputType: quotation.leadTimeInputType || 'date',
        serviceInputType: quotation.serviceInputType || 'date',
        rentalInputType: quotation.rentalInputType || 'date',
        
        // Purchase type dates
        leadTime: quotation.leadTimeInputType === 'date' && quotation.leadTime 
          ? formatDate(quotation.leadTime)
          : '',
        leadTimeMonths: (quotation.leadTimeInputType === 'months' || quotation.leadTimeInputType === 'duration') && quotation.leadTimeMonths
          ? quotation.leadTimeMonths
          : null,
        
        // Rental type dates
        rentalStartDate: quotation.rentalInputType === 'date' && quotation.rentalStartDate 
          ? formatDate(quotation.rentalStartDate)
          : '',
        rentalEndDate: quotation.rentalInputType === 'date' && quotation.rentalEndDate 
          ? formatDate(quotation.rentalEndDate)
          : '',
        rentalDuration: quotation.rentalInputType === 'duration' && quotation.rentalDuration
          ? quotation.rentalDuration
          : null,
        rentalDurationUnit: quotation.rentalInputType === 'duration' && quotation.rentalDurationUnit
          ? quotation.rentalDurationUnit
          : null,
        
        // Scaffolding service type dates
        serviceDate: quotation.serviceInputType === 'date' && quotation.serviceDate
          ? formatDate(quotation.serviceDate)
          : '',
        serviceStartDate: quotation.serviceInputType === 'range' && quotation.serviceStartDate
          ? formatDate(quotation.serviceStartDate)
          : '',
        serviceEndDate: quotation.serviceInputType === 'range' && quotation.serviceEndDate
          ? formatDate(quotation.serviceEndDate)
          : '',
        
        // Multiple dates support
        multipleDatesEnabled: quotation.multipleDates && quotation.multipleDates.length > 0,
        multipleDatesFormatted: quotation.multipleDates && quotation.multipleDates.length > 0 
          ? formatMultipleDatesForPDF(quotation.multipleDates, quotation.quotationType)
          : null,
        
        // Discount data
        discount: quotation.discount || { type: 'none', value: 0 },
        discountAmount: quotation.discountAmount || 0,
        
        // GST data
        gst: quotation.gst || { percentage: 0 },
        gstAmount: quotation.gstAmount || 0,
        
        paymentTerms: quotation.paymentTerms,
        customPaymentTerms: quotation.customPaymentTerms,
        lineItems: quotation.lineItems || [],
        additionalServices: quotation.additionalServices || {},
        customAdditionalServices: quotation.customAdditionalServices || '',
        subtotal: quotation.subtotal || 0,
        additionalServicesTotal: quotation.additionalServicesTotal || 0,
        grandTotal: quotation.grandTotal || 0,
        notes: quotation.notes
      };

      await generateQuotationPDF(quotationData);
      message.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const handleSaveQuotation = (quotation) => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedQuotation(null);
    // Update stats with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getQuotationStats(options).then(setStats);
  };

  const handleCancelModal = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowCreateInvoiceModal(false);
    setShowWhatsAppModal(false);
    setShowEmailModal(false);
    setShowQuoteRequestModal(false);
    setShowCSVImportModal(false);
    setSelectedQuotation(null);
    setSelectedQuotationForInvoice(null);
    setWhatsAppQuotation(null);
    setEmailQuotation(null);
    setSelectedQuoteRequest(null);
  };

  const handleCSVImport = () => {
    setShowCSVImportModal(true);
  };

  const handleCSVImportSuccess = (results) => {
    setShowCSVImportModal(false);
    message.success(`Successfully imported ${results.successful} quotation(s)`);
    
    // Refresh stats with current date range
    const options = {};
    if (dateRange?.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange?.endDate) {
      options.endDate = dateRange.endDate;
    }
    getQuotationStats(options).then(setStats);
  };

  const handleCreateFromQuoteRequest = () => {
    setShowQuoteRequestModal(true);
  };

  const handleSelectQuoteRequest = (quoteRequest) => {
    setSelectedQuoteRequest(quoteRequest);
    setShowQuoteRequestModal(false);
    setShowCreateModal(true);
  };

  const handleCreateInvoice = (quotation) => {
    setSelectedQuotationForInvoice(quotation);
    setShowCreateInvoiceModal(true);
  };

  const handleSaveInvoice = (invoice) => {
    setShowCreateInvoiceModal(false);
    setSelectedQuotationForInvoice(null);
    message.success('Invoice created successfully');
    // Refresh stats with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getQuotationStats(options).then(setStats);
  };

  // Helper function to find invoice for a quotation
  const getInvoiceForQuotation = (quotationId) => {
    return invoices.find(invoice => invoice.quotationId === quotationId);
  };

  const handleSendWhatsApp = (quotation) => {
    setWhatsAppQuotation(quotation);
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppSendSuccess = () => {
    setShowWhatsAppModal(false);
    setWhatsAppQuotation(null);
    // Refresh data to get updated status with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getQuotationStats(options).then(setStats);
  };

  const handleSendEmail = (quotation) => {
    setEmailQuotation(quotation);
    setShowEmailModal(true);
  };

  const handleEmailSendSuccess = () => {
    setShowEmailModal(false);
    setEmailQuotation(null);
    // Refresh data to get updated status with current date range
    const options = {};
    if (dateRange.startDate) {
      options.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      options.endDate = dateRange.endDate;
    }
    getQuotationStats(options).then(setStats);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getStatusTag = (status) => {
    const color = QUOTATION_STATUS_COLORS[status];
    const label = QUOTATION_STATUS_LABELS[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const expandedRowRender = (record) => {
    const lineItems = record.lineItems || [];
    const additionalServices = record.additionalServices || {};
    
    const selectedServices = Object.entries(additionalServices)
      .filter(([key, service]) => service.selected)
      .map(([key, service]) => ({ key, ...service }));

    return (
      <div style={{ margin: '16px 0' }}>
        <Row gutter={[24, 16]}>
          <Col span={12}>
            <Card title="Customer Details" size="small">
              <p><strong>Company:</strong> {record.companyName}</p>
              <p><strong>Contact:</strong> {record.attn}</p>
              <p><strong>Phone:</strong> {record.tel}</p>
              <p><strong>Email:</strong> {record.email}</p>
              {record.billingAddress && <p><strong>Billing Address:</strong> {record.billingAddress}</p>}
              <p><strong>Type:</strong> {QUOTATION_TYPE_LABELS[record.quotationType]}</p>
              
              {/* Display dates using unified date system */}
              <div>
                <p><strong>Dates:</strong> {(() => {
                  // Use unified date system first
                  if (usesUnifiedDates(record)) {
                    return getDatesForPDF(record) || 'N/A';
                  }

                  // Fallback to legacy date system for backward compatibility
                  // Check for multiple dates first
                  if (record.multipleDates && record.multipleDates.length > 0) {
                    const formattedMultipleDates = formatMultipleDatesForPDF(record.multipleDates, record.quotationType);
                    if (formattedMultipleDates) {
                      return formattedMultipleDates;
                    }
                  }

                  // Fallback to single date logic
                  // Purchase type dates
                  if (record.quotationType === 'purchase') {
                    if (record.leadTimeInputType === 'months' && record.leadTimeMonths) {
                      return `${record.leadTimeMonths} month${record.leadTimeMonths > 1 ? 's' : ''}`;
                    }
                    if (record.leadTimeInputType === 'tbc') {
                      return 'TBC';
                    }
                    if (record.leadTime) {
                      return moment(record.leadTime).format('DD/MM/YYYY');
                    }
                  }

                  // Scaffolding service type dates
                  if (record.quotationType === 'scaffolding_service') {
                    if (record.serviceInputType === 'date' && record.serviceDate) {
                      return moment(record.serviceDate).format('DD/MM/YYYY');
                    }
                    if (record.serviceInputType === 'range' && record.serviceStartDate && record.serviceEndDate) {
                      return `${moment(record.serviceStartDate).format('DD/MM/YYYY')}-${moment(record.serviceEndDate).format('DD/MM/YYYY')}`;
                    }
                    if (record.serviceInputType === 'tbc') {
                      return 'TBC';
                    }
                  }

                  // Rental type dates
                  if (record.quotationType === 'rental' || record.quotationType === 'rental_extension') {
                    if (record.rentalInputType === 'date' && record.rentalStartDate && record.rentalEndDate) {
                      return `${moment(record.rentalStartDate).format('DD/MM/YYYY')}-${moment(record.rentalEndDate).format('DD/MM/YYYY')}`;
                    }
                    if (record.rentalInputType === 'duration' && record.rentalDuration && record.rentalDurationUnit) {
                      return `${record.rentalDuration} ${record.rentalDurationUnit}`;
                    }
                  }

                  // Legacy service dates (for backward compatibility)
                  if (record.serviceStartDate && record.serviceEndDate && !record.serviceInputType) {
                    return `${moment(record.serviceStartDate).format('DD/MM/YYYY')}-${moment(record.serviceEndDate).format('DD/MM/YYYY')}`;
                  }

                  return 'N/A';
                })()}</p>
              </div>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Pricing Summary" size="small">
              <p><strong>Subtotal:</strong> {formatCurrency(record.subtotal)}</p>
              <p><strong>Additional Services:</strong> {formatCurrency(record.additionalServicesTotal)}</p>
              <p><strong>Grand Total:</strong> <Text strong>{formatCurrency(record.grandTotal)}</Text></p>
              <p><strong>Payment Terms:</strong> {record.customPaymentTerms || record.paymentTerms}</p>
            </Card>
          </Col>
        </Row>
        
        {lineItems.length > 0 && (
          <Card title="Line Items" size="small" style={{ marginTop: 16 }}>
            <Table
              dataSource={lineItems}
              pagination={false}
              size="small"
              columns={[
                { title: '#', dataIndex: 'id', key: 'id', width: 50 },
                { title: 'Description', dataIndex: 'description', key: 'description' },
                { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 100 },
                { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', width: 120, render: formatCurrency },
                { title: 'Total', dataIndex: 'total', key: 'total', width: 120, render: formatCurrency }
              ]}
            />
          </Card>
        )}

        {selectedServices.length > 0 && (
          <Card title="Additional Services" size="small" style={{ marginTop: 16 }}>
            {selectedServices.map((service, index) => (
              <p key={index}>{service.key}: {formatCurrency(service.price)}</p>
            ))}
          </Card>
        )}

        {record.notes && (
          <Card title="Notes" size="small" style={{ marginTop: 16 }}>
            <p>{record.notes}</p>
          </Card>
        )}

        {record.internalRemarks && (
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#1890ff' }}>🔒</span>
                <span>Internal Remarks (Admin Only)</span>
              </div>
            } 
            size="small" 
            style={{ 
              marginTop: 16,
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f'
            }}
          >
            <div style={{ 
              marginBottom: 8, 
              fontSize: '11px', 
              color: '#666',
              fontStyle: 'italic'
            }}>
              Private notes for internal reference only. Not visible in PDFs or client communications.
            </div>
            <p style={{ margin: 0, backgroundColor: '#fff', padding: '8px', borderRadius: '4px' }}>
              {record.internalRemarks}
            </p>
          </Card>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: 'Quotation #',
      dataIndex: 'quotationNumber',
      key: 'quotationNumber',
      width: 150,
      render: (text, record) => {
        const relatedInvoice = getInvoiceForQuotation(record.id);
        
        return (
          <div>
            <Text strong>{text}</Text>
            {record.quoteRequestId && (
              <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                From Request: {record.quoteRequestId.slice(-8)}
              </div>
            )}
            {relatedInvoice ? (
              <div style={{ fontSize: '11px', color: '#1890ff', marginTop: '2px' }}>
                Invoice: {relatedInvoice.invoiceNumber}
              </div>
            ) : (
              record.status !== 'rejected' && (
                <div style={{ marginTop: '4px' }}>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => handleCreateInvoice(record)}
                    style={{ 
                      padding: 0, 
                      height: 'auto', 
                      fontSize: '11px',
                      color: '#52c41a'
                    }}
                  >
                    Create Invoice
                  </Button>
                </div>
              )
            )}
          </div>
        );
      }
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 200
    },
    {
      title: 'Contact',
      dataIndex: 'attn',
      key: 'attn',
      width: 150
    },
    {
      title: 'Type',
      dataIndex: 'quotationType',
      key: 'quotationType',
      width: 120,
      render: (type) => QUOTATION_TYPE_LABELS[type] || type
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (_, record) => {
        // Use unified date system first
        if (usesUnifiedDates(record)) {
          return getDatesForPDF(record) || 'N/A';
        }

        // Fallback to legacy date system for backward compatibility
        // Check for multiple dates first
        if (record.multipleDates && record.multipleDates.length > 0) {
          const formattedMultipleDates = formatMultipleDatesForPDF(record.multipleDates, record.quotationType);
          if (formattedMultipleDates) {
            return formattedMultipleDates;
          }
        }

        // Fallback to single date logic
        // Purchase type dates
        if (record.quotationType === 'purchase') {
          if (record.leadTimeInputType === 'months' && record.leadTimeMonths) {
            return `${record.leadTimeMonths} month${record.leadTimeMonths > 1 ? 's' : ''}`;
          }
          if (record.leadTimeInputType === 'tbc') {
            return 'TBC';
          }
          if (record.leadTime) {
            return moment(record.leadTime).format('DD/MM/YYYY');
          }
        }

        // Scaffolding service type dates
        if (record.quotationType === 'scaffolding_service') {
          if (record.serviceInputType === 'date' && record.serviceDate) {
            return moment(record.serviceDate).format('DD/MM/YYYY');
          }
          if (record.serviceInputType === 'range' && record.serviceStartDate && record.serviceEndDate) {
            return `${moment(record.serviceStartDate).format('DD/MM/YY')}-${moment(record.serviceEndDate).format('DD/MM/YY')}`;
          }
          if (record.serviceInputType === 'tbc') {
            return 'TBC';
          }
        }

        // Rental type dates
        if (record.quotationType === 'rental' || record.quotationType === 'rental_extension') {
          if (record.rentalInputType === 'date' && record.rentalStartDate && record.rentalEndDate) {
            return `${moment(record.rentalStartDate).format('DD/MM/YY')}-${moment(record.rentalEndDate).format('DD/MM/YY')}`;
          }
          if (record.rentalInputType === 'duration' && record.rentalDuration && record.rentalDurationUnit) {
            return `${record.rentalDuration} ${record.rentalDurationUnit}`;
          }
        }

        // Legacy service dates (for backward compatibility)
        if (record.serviceStartDate && record.serviceEndDate && !record.serviceInputType) {
          return `${moment(record.serviceStartDate).format('DD/MM/YY')}-${moment(record.serviceEndDate).format('DD/MM/YY')}`;
        }

        return 'N/A';
      }
    },
    {
      title: 'Total Amount',
      dataIndex: 'grandTotal',
      key: 'grandTotal',
      width: 120,
      render: formatCurrency
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: getStatusTag
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: formatDate
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEditQuotation(record)}
            />
          </Tooltip>
          <Tooltip title="Generate PDF">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleGeneratePDF(record)}
            />
          </Tooltip>
          <Tooltip title={record.email ? "Send via Email" : "No email address available"}>
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={() => handleSendEmail(record)}
              disabled={!record.email}
              style={{
                color: record.email ? '#1890ff' : undefined
              }}
            />
          </Tooltip>
          <Tooltip title={record.tel ? "Send via WhatsApp" : "No phone number available"}>
            <Button
              type="text"
              icon={<WhatsAppOutlined />}
              onClick={() => handleSendWhatsApp(record)}
              disabled={!record.tel}
              style={{
                color: record.tel ? '#25D366' : undefined
              }}
            />
          </Tooltip>
          <Select
            value={record.status}
            onChange={(value) => handleStatusChange(record.id, value)}
            size="small"
            style={{ width: 90 }}
          >
            {Object.entries(QUOTATION_STATUS_LABELS).map(([key, label]) => (
              <Option key={key} value={key}>{label}</Option>
            ))}
          </Select>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteQuotation(record.id)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  // Mobile card component
  const renderMobileCard = (quotation) => {
    const relatedInvoice = getInvoiceForQuotation(quotation.id);
    
    return (
      <div key={quotation.id} className="quotation-mobile-card">
        <div className="quotation-mobile-header">
          <div className="quotation-mobile-title">
            {quotation.quotationNumber}
            {quotation.quoteRequestId && (
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                (From Request)
              </span>
            )}
          </div>
          <div className="quotation-mobile-meta">
            <span className="quotation-mobile-company">{quotation.companyName}</span>
            <span className="quotation-mobile-amount">{formatCurrency(quotation.grandTotal)}</span>
          </div>
        </div>
        
        <div className="quotation-mobile-body">
          <div className="quotation-mobile-row">
            <span className="quotation-mobile-label">Contact:</span>
            <span className="quotation-mobile-value">{quotation.attn}</span>
          </div>
          <div className="quotation-mobile-row">
            <span className="quotation-mobile-label">Type:</span>
            <span className="quotation-mobile-value">{QUOTATION_TYPE_LABELS[quotation.quotationType] || quotation.quotationType}</span>
          </div>
          <div className="quotation-mobile-row">
            <span className="quotation-mobile-label">Date:</span>
            <span className="quotation-mobile-value">
              {(() => {
                // Use unified date system first
                if (usesUnifiedDates(quotation)) {
                  return getDatesForPDF(quotation) || 'N/A';
                }

                // Fallback to legacy date system for backward compatibility
                // Check for multiple dates first
                if (quotation.multipleDates && quotation.multipleDates.length > 0) {
                  const formattedMultipleDates = formatMultipleDatesForPDF(quotation.multipleDates, quotation.quotationType);
                  if (formattedMultipleDates) {
                    return formattedMultipleDates;
                  }
                }

                // Fallback to single date logic
                // Purchase type dates
                if (quotation.quotationType === 'purchase') {
                  if (quotation.leadTimeInputType === 'months' && quotation.leadTimeMonths) {
                    return `${quotation.leadTimeMonths} month${quotation.leadTimeMonths > 1 ? 's' : ''}`;
                  }
                  if (quotation.leadTimeInputType === 'tbc') {
                    return 'TBC';
                  }
                  if (quotation.leadTime) {
                    return moment(quotation.leadTime).format('DD/MM/YYYY');
                  }
                }

                // Scaffolding service type dates
                if (quotation.quotationType === 'scaffolding_service') {
                  if (quotation.serviceInputType === 'date' && quotation.serviceDate) {
                    return moment(quotation.serviceDate).format('DD/MM/YYYY');
                  }
                  if (quotation.serviceInputType === 'range' && quotation.serviceStartDate && quotation.serviceEndDate) {
                    return `${moment(quotation.serviceStartDate).format('DD/MM/YY')}-${moment(quotation.serviceEndDate).format('DD/MM/YY')}`;
                  }
                  if (quotation.serviceInputType === 'tbc') {
                    return 'TBC';
                  }
                }

                // Rental type dates
                if (quotation.quotationType === 'rental' || quotation.quotationType === 'rental_extension') {
                  if (quotation.rentalInputType === 'date' && quotation.rentalStartDate && quotation.rentalEndDate) {
                    return `${moment(quotation.rentalStartDate).format('DD/MM/YY')}-${moment(quotation.rentalEndDate).format('DD/MM/YY')}`;
                  }
                  if (quotation.rentalInputType === 'duration' && quotation.rentalDuration && quotation.rentalDurationUnit) {
                    return `${quotation.rentalDuration} ${quotation.rentalDurationUnit}`;
                  }
                }

                // Legacy service dates (for backward compatibility)
                if (quotation.serviceStartDate && quotation.serviceEndDate && !quotation.serviceInputType) {
                  return `${moment(quotation.serviceStartDate).format('DD/MM/YY')}-${moment(quotation.serviceEndDate).format('DD/MM/YY')}`;
                }

                return 'N/A';
              })()}
            </span>
          </div>
          <div className="quotation-mobile-row">
            <span className="quotation-mobile-label">Status:</span>
            <span className="quotation-mobile-value">
              <Tag color={QUOTATION_STATUS_COLORS[quotation.status]}>
                {QUOTATION_STATUS_LABELS[quotation.status]}
              </Tag>
            </span>
          </div>
          <div className="quotation-mobile-row">
            <span className="quotation-mobile-label">Created:</span>
            <span className="quotation-mobile-value">{formatDate(quotation.createdAt)}</span>
          </div>
          {relatedInvoice && (
            <div className="quotation-mobile-row">
              <span className="quotation-mobile-label">Invoice:</span>
              <span className="quotation-mobile-value" style={{ color: '#1890ff' }}>
                {relatedInvoice.invoiceNumber}
              </span>
            </div>
          )}
          {quotation.internalRemarks && (
            <div className="quotation-mobile-row" style={{ 
              backgroundColor: '#f6ffed', 
              padding: '8px', 
              borderRadius: '4px',
              border: '1px solid #b7eb8f',
              marginTop: '8px'
            }}>
              <span className="quotation-mobile-label" style={{ color: '#1890ff' }}>
                🔒 Internal Remarks:
              </span>
              <span className="quotation-mobile-value" style={{ 
                fontSize: '12px',
                fontStyle: 'italic',
                display: 'block',
                marginTop: '4px'
              }}>
                {quotation.internalRemarks}
              </span>
            </div>
          )}
        </div>
        
        <div className="quotation-mobile-actions">
          <div className="quotation-mobile-actions-row">
            <button
              className="quotation-mobile-btn primary"
              onClick={() => handleEditQuotation(quotation)}
            >
              <EditOutlined /> Edit
            </button>
            <button
              className="quotation-mobile-btn"
              onClick={() => handleGeneratePDF(quotation)}
            >
              <DownloadOutlined /> PDF
            </button>
            {quotation.email && (
              <button
                className="quotation-mobile-btn"
                onClick={() => handleSendEmail(quotation)}
              >
                <MailOutlined /> Email
              </button>
            )}
            {quotation.tel && (
              <button
                className="quotation-mobile-btn whatsapp"
                onClick={() => handleSendWhatsApp(quotation)}
              >
                <WhatsAppOutlined /> WhatsApp
              </button>
            )}
          </div>
          <div className="quotation-mobile-actions-row">
            {!relatedInvoice && quotation.status !== 'rejected' && (
              <button
                className="quotation-mobile-btn primary"
                onClick={() => handleCreateInvoice(quotation)}
              >
                Create Invoice
              </button>
            )}
            <button
              className="quotation-mobile-btn danger"
              onClick={() => handleDeleteQuotation(quotation.id)}
            >
              <DeleteOutlined /> Delete
            </button>
          </div>
          <select
            className="quotation-mobile-status-select"
            value={quotation.status}
            onChange={(e) => handleStatusChange(quotation.id, e.target.value)}
          >
            {Object.entries(QUOTATION_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  return (
    <div className="quotation-list-container">
      <div className="section-header-redesigned">
        <div className="header-top">
          <Title level={2}>Quotation Management</Title>
        </div>
        
        <div className="stats-summary">
          <div className="stat-item">
            <span className="stat-number">{stats.total || 0}</span>
            <span className="stat-label">Total Quotations</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: QUOTATION_STATUS_COLORS.draft}}>{stats.draft || 0}</span>
            <span className="stat-label">Draft</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: QUOTATION_STATUS_COLORS.sent}}>{stats.sent || 0}</span>
            <span className="stat-label">Sent</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: QUOTATION_STATUS_COLORS.accepted}}>{stats.accepted || 0}</span>
            <span className="stat-label">Accepted</span>
          </div>
          <div className="stat-item">
            <span className="stat-number" style={{color: QUOTATION_STATUS_COLORS.rejected}}>{stats.rejected || 0}</span>
            <span className="stat-label">Rejected</span>
          </div>
        </div>
      </div>

      {/* Mobile Stats */}
      <div className="quotation-stats-mobile">
        <div className="quotation-stat-mobile">
          <span className="quotation-stat-number">{stats.total || 0}</span>
          <span className="quotation-stat-label">Total</span>
        </div>
        <div className="quotation-stat-mobile">
          <span className="quotation-stat-number" style={{color: QUOTATION_STATUS_COLORS.draft}}>{stats.draft || 0}</span>
          <span className="quotation-stat-label">Draft</span>
        </div>
        <div className="quotation-stat-mobile">
          <span className="quotation-stat-number" style={{color: QUOTATION_STATUS_COLORS.sent}}>{stats.sent || 0}</span>
          <span className="quotation-stat-label">Sent</span>
        </div>
        <div className="quotation-stat-mobile">
          <span className="quotation-stat-number" style={{color: QUOTATION_STATUS_COLORS.accepted}}>{stats.accepted || 0}</span>
          <span className="quotation-stat-label">Accepted</span>
        </div>
        <div className="quotation-stat-mobile">
          <span className="quotation-stat-number" style={{color: QUOTATION_STATUS_COLORS.rejected}}>{stats.rejected || 0}</span>
          <span className="quotation-stat-label">Rejected</span>
        </div>
      </div>

      {/* Mobile Create Buttons */}
      <div className="quotation-create-buttons-mobile">
        <button
          className="quotation-create-btn-mobile secondary"
          onClick={handleCreateFromQuoteRequest}
          disabled={availableQuoteRequests.length === 0}
        >
          <PlusOutlined /> From Quote Request
        </button>
        <button
          className="quotation-create-btn-mobile primary"
          onClick={handleCreateQuotation}
        >
          <PlusOutlined /> Create Quotation
        </button>
      </div>

      {/* Mobile Filters */}
      <div className="quotation-filters-mobile">
        <input
          className="quotation-search-mobile"
          placeholder="Search quotations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="quotation-filters-row">
          <select
            className="quotation-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            {Object.entries(QUOTATION_STATUS_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select
            className="quotation-filter-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All Types</option>
            {Object.entries(QUOTATION_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Desktop View */}
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Input
                placeholder="Search quotations..."
                prefix={<SearchOutlined />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col span={4}>
              <Select
                placeholder="Status"
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">All Status</Option>
                {Object.entries(QUOTATION_STATUS_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={4}>
              <Select
                placeholder="Type"
                value={typeFilter}
                onChange={setTypeFilter}
                style={{ width: '100%' }}
              >
                <Option value="all">All Types</Option>
                {Object.entries(QUOTATION_TYPE_LABELS).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>
            </Col>
            <Col span={8}>
              <div style={{ textAlign: 'right' }}>
                <Space>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={handleCSVImport}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    📊 CSV Import
                  </Button>
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleCreateFromQuoteRequest}
                    disabled={availableQuoteRequests.length === 0}
                  >
                    From Quote Request
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateQuotation}
                  >
                    Create Quotation
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        </div>

        <Table
          dataSource={filteredQuotations}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            total: filteredQuotations.length,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} quotations`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({
                ...prev,
                current: page,
                pageSize: pageSize
              }));
            },
            onShowSizeChange: (current, size) => {
              setPagination(prev => ({
                ...prev,
                current: 1, // Reset to first page when changing page size
                pageSize: size
              }));
            }
          }}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpandedRowsChange: setExpandedRowKeys,
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={(e) => onExpand(record, e)}
                />
              )
          }}
        />
      </Card>

      {/* Mobile View */}
      <div className="quotation-mobile-view">
        {loading ? (
          <div className="quotation-mobile-loading">Loading quotations...</div>
        ) : filteredQuotations.length === 0 ? (
          <div className="quotation-mobile-empty">
            <div className="quotation-mobile-empty-icon">📄</div>
            <div className="quotation-mobile-empty-text">No quotations found</div>
            <div className="quotation-mobile-empty-subtext">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.' 
                : 'Create your first quotation to get started.'}
            </div>
          </div>
        ) : (
          filteredQuotations.map(renderMobileCard)
        )}
      </div>

      {/* Create Quotation Modal */}
      <Modal
        title="Create New Quotation"
        open={showCreateModal}
        onCancel={handleCancelModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        <QuotationForm
          linkedQuoteRequest={selectedQuoteRequest}
          onSave={handleSaveQuotation}
          onCancel={handleCancelModal}
        />
      </Modal>

      {/* Edit Quotation Modal */}
      <Modal
        title="Edit Quotation"
        open={showEditModal}
        onCancel={handleCancelModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {selectedQuotation && (
          <QuotationForm
            quotation={selectedQuotation}
            onSave={handleSaveQuotation}
            onCancel={handleCancelModal}
          />
        )}
      </Modal>

      {/* Create Invoice Modal */}
      <Modal
        title="Create Invoice from Quotation"
        open={showCreateInvoiceModal}
        onCancel={handleCancelModal}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {selectedQuotationForInvoice && (
          <InvoiceForm
            linkedQuotation={selectedQuotationForInvoice}
            onSave={handleSaveInvoice}
            onCancel={handleCancelModal}
          />
        )}
      </Modal>

      {/* WhatsApp Modal */}
      {whatsAppQuotation && (
        <WhatsAppModal
          visible={showWhatsAppModal}
          onCancel={() => setShowWhatsAppModal(false)}
          documentType="quotation"
          documentData={whatsAppQuotation}
          phoneNumber={whatsAppQuotation.tel}
          onSendSuccess={handleWhatsAppSendSuccess}
        />
      )}

      {/* Email Modal */}
      {emailQuotation && (
        <EmailModal
          visible={showEmailModal}
          onCancel={() => setShowEmailModal(false)}
          documentType="quotation"
          documentData={emailQuotation}
          onSendSuccess={handleEmailSendSuccess}
        />
      )}

      {/* CSV Import Modal */}
      <Modal
        title="Import Historical Quotations"
        open={showCSVImportModal}
        onCancel={handleCancelModal}
        footer={null}
        width={900}
        destroyOnClose
      >
        <CSVQuotationImport
          onClose={handleCancelModal}
          onSuccess={handleCSVImportSuccess}
        />
      </Modal>

      {/* Quote Request Selection Modal */}
      <Modal
        title="Select Quote Request"
        open={showQuoteRequestModal}
        onCancel={handleCancelModal}
        footer={null}
        width={800}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Select a quote request to create a quotation from. Only quote requests that don't have quotations created and are not closed are shown.
          </Text>
        </div>
        
        {availableQuoteRequests.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <Title level={4}>No Available Quote Requests</Title>
            <Text type="secondary">
              All quote requests either have quotations created or are closed.
            </Text>
          </div>
        ) : (
          <Table
            dataSource={availableQuoteRequests}
            pagination={false}
            rowKey="id"
            size="small"
            columns={[
              {
                title: 'Customer',
                dataIndex: 'customerName',
                key: 'customerName',
                render: (text, record) => (
                  <div>
                    <Text strong>{text}</Text>
                    {record.companyName && record.companyName !== '' && record.companyName !== 'N/A' && (
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {record.companyName}
                      </div>
                    )}
                  </div>
                )
              },
              {
                title: 'Contact',
                key: 'contact',
                render: (_, record) => (
                  <div>
                    <div>{record.customerEmail}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {record.customerPhone}
                    </div>
                  </div>
                )
              },
              {
                title: 'Products',
                dataIndex: 'products',
                key: 'products',
                render: (products) => {
                  if (!products || products.length === 0) return 'No products';
                  return (
                    <div>
                      {products.map((product, index) => {
                        const workingHeight = product.workingHeight;
                        const heightDisplay = (typeof workingHeight === 'number' && workingHeight > 0) || (typeof workingHeight === 'string' && workingHeight !== '')
                          ? `${workingHeight}m` 
                          : 'Not specified';
                        return (
                          <div key={index} style={{ fontSize: '12px', marginBottom: '2px' }}>
                            <strong>Working Height:</strong> {heightDisplay}, 
                            <strong> Width:</strong> {product.width || 'Not specified'}, 
                            <strong> Qty:</strong> {product.quantity || 'Not specified'}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
              },
              {
                title: 'Details',
                key: 'details',
                render: (_, record) => {
                  const formatDate = (dateStr) => {
                    if (!dateStr || dateStr === '') return '';
                    // Handle ISO format (YYYY-MM-DD) and convert to DD/MM/YYYY for display
                    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                      const [year, month, day] = dateStr.split('-');
                      return `${day}/${month}/${year}`;
                    }
                    return dateStr;
                  };

                  return (
                    <div style={{ fontSize: '12px' }}>
                      {record.enquiryType === 'purchase' && (
                        <div>
                          {/* For Purchase Enquiries: Only show delivery date OR "no fixed date" status */}
                          {record.datePurchase && record.datePurchase !== '' ? (
                            <div><strong>Delivery Date:</strong> {formatDate(record.datePurchase)}</div>
                          ) : (record.purchaseCheckbox === 'yes' || record.purchaseCheckbox === true) ? (
                            <div><strong>Delivery Date:</strong> No fixed date</div>
                          ) : (
                            <div><strong>Delivery Date:</strong> Not specified</div>
                          )}
                        </div>
                      )}
                      {record.enquiryType === 'rental' && (
                        <div>
                          {/* For Rental Enquiries: Only show rental date range OR duration + duration type */}
                          {record.dateRental && Array.isArray(record.dateRental) && record.dateRental.length === 2 && record.dateRental[0] !== '' ? (
                            <div><strong>Rental Period:</strong> {formatDate(record.dateRental[0])} to {formatDate(record.dateRental[1])}</div>
                          ) : (record.dateDuration && record.dateDuration !== '' && record.durationType && record.durationType !== '') ? (
                            <div><strong>Duration:</strong> {record.dateDuration} {record.durationType}(s)</div>
                          ) : (
                            <div><strong>Rental Period:</strong> Not specified</div>
                          )}
                        </div>
                      )}
                      {record.message && record.message !== '' && (
                        <div style={{ marginTop: '4px' }}><strong>Message:</strong> {record.message}</div>
                      )}
                    </div>
                  );
                }
              },
              {
                title: 'Type',
                dataIndex: 'enquiryType',
                key: 'enquiryType',
                render: (type) => (
                  <Tag color={type === 'rental' ? 'blue' : 'green'}>
                    {type === 'rental' ? 'Rental' : 'Purchase'}
                  </Tag>
                )
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => (
                  <Tag color={QUOTE_STATUS_COLORS[status]}>
                    {QUOTE_STATUS_LABELS[status]}
                  </Tag>
                )
              },
              {
                title: 'Created',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => moment(date).format('DD/MM/YYYY')
              },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                  <Button
                    type="primary"
                    size="small"
                    onClick={() => handleSelectQuoteRequest(record)}
                  >
                    Create Quotation
                  </Button>
                )
              }
            ]}
          />
        )}
      </Modal>
    </div>
  );
};

export default QuotationList;
